import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { GetAppObject } from "./ImageSuite";
import "../../SharedSuite/Suites.css";
import { SuiteAppSidebarItem } from "../../SharedSuite/Navbar/SuiteAppSidebarItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useLocation } from "react-router-dom";
import { HistoryButton } from "../../../Layouts/SuiteHistoryLayout/Shared/HistoryButton";
import { useAppSelector } from "../../../store/hooks";

export interface ActiveItemInterface {
  type: number;
  title: string;
  icon: any;
}

interface ImageSuiteSidebarProps {
  activeItem: ActiveItemInterface;
  TextSuitesApps: any[];
  setActiveItem: (item: ActiveItemInterface) => void;
  setInputValue?: (item: string) => void;
  setSelectedFromDropdownItem?: (name: string) => void;
  setSelectedToDropdownItem?: (name: string) => void;
}

export const ImageSuiteSidebar = ({
  setActiveItem,
  activeItem,
  setSelectedFromDropdownItem,
  setInputValue,
  TextSuitesApps,
}: ImageSuiteSidebarProps) => {
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [visibleApps, setVisibleApps] = useState<any[]>([]);
  const [hiddenApps, setHiddenApps] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isUserAuthenticated = useAppSelector((s) => s.User.IsAuthenticated);

  const isMobile = useMediaQuery("(max-width: 960px)");

  useEffect(() => {
    if (location.state && location.state.imageSuiteAppName) {
      handleSelectApp(location.state.imageSuiteAppName);
    }
  }, [location.state]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSetActiveItem = (name: string) => {
    if (name !== activeItem.title) {
      const appObject = GetAppObject(name);
      if (appObject) setActiveItem({ ...appObject });
      if (setInputValue && setSelectedFromDropdownItem) {
        setInputValue("");
        setSelectedFromDropdownItem("");
      }
    }
  };

  const handleSelectApp = (appTitle: string) => {
    handleSetActiveItem(appTitle);

    const foundInHidden = hiddenApps.find((app) => app.title === appTitle);
    if (foundInHidden) {
      setVisibleApps((prev) => {
        const newVisible = [...prev];
        const lastVisible = newVisible.pop(); // Remove the last shown app
        newVisible.push(foundInHidden); // Add the selected hidden app

        setHiddenApps((prevHidden) => {
          let newHidden = prevHidden.filter((app) => app.title !== appTitle);
          if (lastVisible) newHidden.push(lastVisible);
          return newHidden;
        });

        return newVisible;
      });
    }
    handleClose();
  };

  // Layout logic for visible/hidden apps
  useEffect(() => {
    if (isMobile) {
      // On mobile — show all apps, no dropdown.
      setVisibleApps(TextSuitesApps);
      setHiddenApps([]);
      return;
    }

    const observer = new ResizeObserver(() => {
      if (sidebarRef.current) {
        const containerHeight = sidebarRef.current.clientHeight;
        const maxVisible = Math.floor((containerHeight * 0.7) / 56);

        if (TextSuitesApps.length > maxVisible) {
          setVisibleApps(TextSuitesApps.slice(0, maxVisible));
          setHiddenApps(TextSuitesApps.slice(maxVisible));
        } else {
          setVisibleApps(TextSuitesApps);
          setHiddenApps([]);
        }
      }
    });

    if (sidebarRef.current) observer.observe(sidebarRef.current);

    return () => {
      if (sidebarRef.current) observer.unobserve(sidebarRef.current);
    };
  }, [TextSuitesApps, isMobile]);

  return (
    <Box
      ref={sidebarRef}
      className={`app-suites-sidebar ${
        window.location.href.includes("image-suite") ? "image-suite" : ""
      }`}
    >
      <Box className="video-suite-apps-container" 
       sx={{
          width: "100%",
          flexGrow: 1,
        }}
      >

      {visibleApps.map((app, i) => (
        <Box
          key={i}
          onClick={() => handleSelectApp(app.title)}
          sx={{
            width: isMobile ? "auto" : "100%",
            flexShrink: 0,
            cursor: "pointer",
            my: 1,
          }}
        >
          <SuiteAppSidebarItem
            title={app.title}
            icon={app.icon}
            isActive={activeItem.title === app.title}
          />
        </Box>
      ))}

      {!isMobile && hiddenApps.length > 0 && (
        <Box sx={{ width: "100%" }}>
          <Button
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
            sx={{
              textTransform: "inherit",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: "0.3s",
              backgroundColor: open ? "var(--M-background98)" : "transparent",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(118, 207, 235, 0.05)" },
            }}
          >
            <MoreHorizIcon
              sx={{ color: "var(--Main_icons_color)", opacity: 0.7 }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: "4px",
              }}
            >
              <Typography className="hidden-apps-title">More</Typography>
              <Box className="hidden-apps-length">
                <Typography>{hiddenApps.length}</Typography>
              </Box>
            </Box>
          </Button>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
              sx: {
                backgroundColor: "var(--M_background64)",
                color: "var(--Main_icons_color)",
              },
            }}
            sx={{
              "& .MuiList-root": {
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              },
            }}
          >
            <Typography className="hidden-menu-header">More</Typography>
            {hiddenApps.map((app, i) => (
              <MenuItem
                key={i}
                className="hidden-menu-app"
                onClick={() => handleSelectApp(app.title)}
              >
                <Box className="hidden-menu-app-icon">{app.icon}</Box>
                <Box className="hidden-menu-app-name">{app.title}</Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}
      </Box>
      {isUserAuthenticated && (
        <HistoryButton navigateRoute="/suites/history/image" />
      )}
    </Box>
  );
};
